import React, { useState, useEffect } from 'react';
import { RESEARCH_TYPES } from '../../utils/sosaSlots.ts';
import { Loader2 } from 'lucide-react';
import { Project } from '../../types.ts';
import { ApiService } from '../../services/ApiService.ts';
import { useTranslation } from '../../i18n.ts';
import { useToast } from '../shared/Toast.tsx';
import { Modal } from '../ui/Modal.tsx';
import { useSessionStore } from '../../stores/useSessionStore.ts';
import { useOrganizationStore } from '../../stores/useOrganizationStore.ts';
import { ConfirmDialog } from '../shared/ConfirmDialog.tsx';

interface EditProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
  onUpdated: (updated: Project) => void;
}

const EditProjectModal: React.FC<EditProjectModalProps> = ({
  isOpen,
  onClose,
  project,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useSessionStore();
  const currentOrg = useOrganizationStore((s) => s.currentOrg);
  const orgMembersCount = useOrganizationStore((s) => s.members.length);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [researchType, setResearchType] = useState('kwartierstaat');
  const [projectLanguage, setProjectLanguage] = useState<'nl' | 'en' | 'de' | 'fr'>('nl');
  const initialOrgId = project.organization_id || null;
  const [organizationId, setOrganizationId] = useState<string | null>(initialOrgId);
  const [saving, setSaving] = useState(false);

  // Plan 3 (#778) — bevestig conversion (org ↔ persoonlijk) voordat we
  // de PUT versturen. Twee varianten: informatief (persoonlijk → org) en
  // waarschuwend (org → persoonlijk, anderen verliezen toegang).
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Welke organisatie hoort bij dit project (voor labels). Fallback:
  // currentOrg (als user zelf eigenaar is) of user.organization.
  const orgForLabel = currentOrg || user?.organization || null;

  useEffect(() => {
    if (isOpen && project) {
      setName(project.name);
      setDescription(project.description || '');
      setResearchType((project.settings as any)?.researchType || 'kwartierstaat');
      setProjectLanguage(((project.settings as any)?.display?.language as any) || 'nl');
      setOrganizationId(project.organization_id || null);
    }
  }, [isOpen, project]);

  const orgChanged = organizationId !== initialOrgId;
  const isToOrg = !initialOrgId && !!organizationId; // persoonlijk → org
  const isToPersonal = !!initialOrgId && !organizationId; // org → persoonlijk

  const performSave = async () => {
    setSaving(true);
    try {
      const updates: Partial<Project> = {
        name: name.trim(),
        description: description.trim(),
        settings: {
          ...(project.settings || {}),
          researchType,
          display: { ...((project.settings as any)?.display || {}), language: projectLanguage },
        },
        organization_id: organizationId,
      };
      await ApiService.updateProject(project.id, updates);
      onUpdated({ ...project, ...updates });
      toast(t('projectUpdated') || 'Project bijgewerkt', 'success');
      onClose();
    } catch (err: any) {
      toast(err.message || t('ogProjectUpdateFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    if (orgChanged) {
      setConfirmOpen(true);
      return;
    }
    await performSave();
  };

  const handleConfirmConversion = async () => {
    setConfirmOpen(false);
    await performSave();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} titleId="modal-title-edit-project" size="md">
      <div className="p-6 md:p-8">
        <h2
          id="modal-title-edit-project"
          className="text-xl font-serif font-bold text-pt-forest dark:text-[var(--text-main)] mb-6"
        >
          {t('editProject') || 'Project bewerken'}
        </h2>

        <div className="space-y-5">
          {/* Name */}
          <div>
            <label
              htmlFor="ep-name"
              className="block text-[10px] font-black text-pt-sage-text dark:text-[var(--text-muted)] uppercase tracking-widest mb-2"
            >
              {t('projectName')}
            </label>
            <input
              id="ep-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 bg-pt-cream dark:bg-[var(--bg-surface)] border border-pt-forest/10 dark:border-white/10 rounded-2xl text-sm font-bold text-pt-forest dark:text-[var(--text-main)] focus:ring-4 focus:ring-pt-gold/20 outline-none transition-colors"
              autoFocus
            />
          </div>

          {/* Research Type */}
          <div>
            <label
              htmlFor="ep-type"
              className="block text-[10px] font-black text-pt-sage-text dark:text-[var(--text-muted)] uppercase tracking-widest mb-2"
            >
              {t('researchType')}
            </label>
            <select
              id="ep-type"
              value={researchType}
              onChange={(e) => setResearchType(e.target.value)}
              className="w-full px-4 py-3 bg-pt-cream dark:bg-[var(--bg-surface)] border border-pt-forest/10 dark:border-white/10 rounded-2xl text-sm font-bold text-pt-forest dark:text-[var(--text-main)] focus:ring-4 focus:ring-pt-gold/20 outline-none transition-colors"
            >
              {RESEARCH_TYPES.map((rt) => (
                <option key={rt} value={rt}>
                  {t(`researchType_${rt}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Projecttaal — NC #730: schrijfwijze van landen/provincies (later ook rapporten) */}
          <div>
            <label
              htmlFor="ep-language"
              className="block text-[10px] font-black text-pt-sage-text dark:text-[var(--text-muted)] uppercase tracking-widest mb-2"
            >
              {t('projectLanguage')}
            </label>
            <select
              id="ep-language"
              data-testid="edit-project-language"
              value={projectLanguage}
              onChange={(e) => setProjectLanguage(e.target.value as any)}
              className="w-full px-4 py-3 bg-pt-cream dark:bg-[var(--bg-surface)] border border-pt-forest/10 dark:border-white/10 rounded-2xl text-sm font-bold text-pt-forest dark:text-[var(--text-main)] focus:ring-4 focus:ring-pt-gold/20 outline-none transition-colors"
            >
              {(['nl', 'en', 'de', 'fr'] as const).map((l) => (
                <option key={l} value={l}>
                  {t(`projectLanguage_${l}`)}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-pt-sage-text dark:text-[var(--text-muted)]">
              {t('projectLanguageHint')}
            </p>
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="ep-desc"
              className="block text-[10px] font-black text-pt-sage-text dark:text-[var(--text-muted)] uppercase tracking-widest mb-2"
            >
              {t('description')}
            </label>
            <textarea
              id="ep-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 bg-pt-cream dark:bg-[var(--bg-surface)] border border-pt-forest/10 dark:border-white/10 rounded-2xl text-sm text-pt-forest dark:text-[var(--text-main)] focus:ring-4 focus:ring-pt-gold/20 outline-none transition-colors resize-none"
              placeholder={
                t('projectDescriptionPlaceholder') || 'Beschrijf het doel van dit onderzoek...'
              }
            />
          </div>

          {/* Organization — Plan 3 (#778): toon dropdown alleen voor org-leden */}
          {orgForLabel && (
            <div>
              <label
                htmlFor="ep-org"
                className="block text-[10px] font-black text-pt-sage-text dark:text-[var(--text-muted)] uppercase tracking-widest mb-2"
              >
                {t('projectOrganization')}
              </label>
              <select
                id="ep-org"
                className="w-full px-4 py-3 bg-pt-cream dark:bg-[var(--bg-surface)] border border-pt-forest/10 dark:border-white/10 rounded-2xl text-sm font-bold text-pt-forest dark:text-[var(--text-main)] focus:ring-4 focus:ring-pt-gold/20 outline-none transition-colors"
                value={organizationId || ''}
                onChange={(e) => setOrganizationId(e.target.value || null)}
              >
                <option value="">{t('projectNoOrganization') || 'Persoonlijk'}</option>
                <option value={orgForLabel.id}>{orgForLabel.name}</option>
              </select>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-8">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-white dark:bg-white/5 text-pt-sage-text dark:text-[var(--text-muted)] border border-pt-forest/10 dark:border-white/10 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-pt-forest/5 dark:hover:bg-white/10 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="px-5 py-2.5 bg-pt-forest text-pt-gold rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-pt-forest/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 size={12} className="animate-spin" />}
            {t('save')}
          </button>
        </div>

        {/* Plan 3 (#778) — conversion-confirmation. Twee varianten op
            basis van de richting: persoonlijk → org (informatief) of
            org → persoonlijk (waarschuwend met audit-vermelding). */}
        <ConfirmDialog
          open={confirmOpen}
          title={
            isToOrg
              ? t('ogMoveProjectToOrgTitle', { org: orgForLabel?.name || t('ogOrganizationLower') })
              : t('ogMoveProjectToPersonalTitle')
          }
          description={
            isToOrg ? (
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  {t('ogToOrgAllMembersAccess', {
                    org: orgForLabel?.name || t('ogTheOrganization'),
                  })}
                </li>
                <li>{t('ogToOrgAppearsUnder')}</li>
                <li>{t('ogToOrgYouStayOwner')}</li>
                <li>{t('ogToOrgBrandingVisible')}</li>
              </ul>
            ) : (
              <div className="space-y-2">
                <p className="text-pt-ruby font-semibold">
                  {orgMembersCount > 1
                    ? t('ogOtherMembersLoseAccessN', { n: String(orgMembersCount - 1) })
                    : t('deleteOrgConsequence2')}
                </p>
                <ul className="list-disc pl-5 space-y-1 text-pt-sage">
                  <li>{t('ogToPersonalDisappears')}</li>
                  <li>{t('ogToPersonalPrivate')}</li>
                  <li>{t('ogToPersonalAuditLogged')}</li>
                </ul>
              </div>
            )
          }
          confirmLabel={
            isToOrg
              ? t('ogMoveToOrgName', { org: orgForLabel?.name || t('ogOrganizationLower') })
              : t('organizationConversionToPersonal')
          }
          variant={isToPersonal ? 'danger' : 'default'}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleConfirmConversion}
        />
      </div>
    </Modal>
  );
};

export default React.memo(EditProjectModal);
